/**
 * Markdown reader for a single sheet. URL: /study/:sheetId
 *
 * Loads the markdown via the backend, renders with react-markdown + GFM
 * (tables, strikethrough, task lists). Uses a Tailwind-typography-ish
 * prose style without pulling in the @tailwindcss/typography plugin —
 * keeps the bundle small.
 */

import { useQuery } from "@tanstack/react-query";
import { Navigate, useParams } from "react-router-dom";
import { BackLink } from "@/shared/components/BackLink";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { fetchSheetContent, listSheets } from "@/shared/api";

export function SheetReader() {
  const { sheetId } = useParams<{ sheetId: string }>();
  const meta = useQuery({
    queryKey: ["sheets"],
    queryFn: listSheets,
    staleTime: Infinity,
  });
  const content = useQuery({
    queryKey: ["sheet", sheetId],
    queryFn: () => fetchSheetContent(sheetId!),
    enabled: !!sheetId,
    staleTime: Infinity,
  });

  if (!sheetId) return <Navigate to="/study" replace />;

  const sheet = meta.data?.find((s) => s.id === sheetId);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 space-y-6">
      <header>
        <BackLink to="/study" label="Back to sheets" />
        <h1 className="text-2xl font-bold text-text mt-2">
          {sheet?.displayName ?? "Sheet"}
        </h1>
        {sheet && (
          <div className="text-xs text-text-muted mt-1">
            Sheet {sheet.number.toString().padStart(2, "0")} · {sheet.category}
          </div>
        )}
      </header>

      {content.isLoading && (
        <div className="text-sm text-text-muted">Loading sheet…</div>
      )}
      {content.error && (
        <div className="text-sm text-danger">
          Couldn't load:{" "}
          {content.error instanceof Error
            ? content.error.message
            : "Unknown error"}
        </div>
      )}

      {content.data && (
        <article className="prose-sheet">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {content.data}
          </ReactMarkdown>
        </article>
      )}
    </div>
  );
}
